import { Link } from "react-router-dom";
import { Header } from "@/components/landing/Header";
import { Footer } from "@/components/landing/Footer";
import { SeoHead } from "@/components/seo/SeoHead";
import { StructuredData } from "@/components/seo/StructuredData";
import {
  getBaseUrl,
  buildBreadcrumbSchema,
  buildOrganizationSchema,
  buildSoftwareApplicationSchema,
} from "@/lib/seo";
import { Button } from "@/components/ui/button";
import { Building2, MessageCircle, Zap, ArrowRight } from "lucide-react";

const TITLE = "Agente de IA para SaaS e empresas de serviços | FabChat";
const DESCRIPTION =
  "Agente de IA para SaaS e serviços no WhatsApp e Instagram: onboarding, suporte e qualificação de leads com CRM integrado.";

const BREADCRUMB = [
  { name: "Início", path: "/" },
  { name: "SaaS e serviços", path: "/setores/servicos" },
];

export default function SetoresServicos() {
  const baseUrl = getBaseUrl();
  const breadcrumbSchema = buildBreadcrumbSchema(baseUrl, BREADCRUMB);
  const organizationSchema = buildOrganizationSchema(baseUrl);
  const softwareSchema = buildSoftwareApplicationSchema(baseUrl, DESCRIPTION);

  return (
    <div className="min-h-screen flex flex-col bg-background">
      <SeoHead title={TITLE} description={DESCRIPTION} canonicalPath="/setores/servicos" />
      <StructuredData data={[organizationSchema, softwareSchema, breadcrumbSchema]} />
      <Header />

      <main className="flex-1">
        <section className="container mx-auto px-4 py-16 md:py-24 max-w-4xl">
          <h1 className="text-4xl md:text-5xl font-bold mb-4">Agente de IA para SaaS e serviços</h1>
          <p className="text-lg text-muted-foreground mb-12">
            Atendimento automatizado no WhatsApp e Instagram para SaaS e empresas de serviços: onboarding, suporte, cobrança e vendas com respostas alinhadas ao seu playbook.
          </p>

          <section className="mb-10">
            <h2 className="text-2xl font-semibold mb-4">Por que usar a FabChat em SaaS e serviços</h2>
            <p className="text-muted-foreground mb-4">
              Empresas de software e serviços recebem muitas mensagens sobre planos, suporte técnico, cobrança e onboarding. Com um <Link to="/whatsapp/agente-ia" className="text-primary hover:underline">agente de IA</Link> na FabChat, é possível responder dúvidas frequentes, direcionar para documentação ou humano e manter o tom consistente com a sua marca. As conversas viram leads no <Link to="/integracoes/crm" className="text-primary hover:underline">CRM integrado</Link>, com histórico para a equipe dar sequência em vendas ou suporte.
            </p>
          </section>

          <section className="mb-10">
            <h2 className="text-2xl font-semibold mb-4">Recursos que se encaixam no dia a dia</h2>
            <ul className="space-y-3 text-muted-foreground">
              <li className="flex gap-3">
                <Building2 className="h-5 w-5 text-primary flex-shrink-0 mt-0.5" />
                <span>Onboarding e dúvidas iniciais: respostas automáticas no tom do seu produto.</span>
              </li>
              <li className="flex gap-3">
                <MessageCircle className="h-5 w-5 text-primary flex-shrink-0 mt-0.5" />
                <span>Suporte e cobrança: triagem por conversa e encaminhamento para humano quando necessário.</span>
              </li>
              <li className="flex gap-3">
                <Zap className="h-5 w-5 text-primary flex-shrink-0 mt-0.5" />
                <span>Upsell e qualificação: follow-up automático e leads organizados no funil.</span>
              </li>
            </ul>
          </section>

          <section className="mb-10">
            <h2 className="text-2xl font-semibold mb-4">Respostas consistentes com o playbook</h2>
            <p className="text-muted-foreground mb-4">
              Você configura o comportamento e as regras do agente para que as respostas estejam alinhadas ao que sua equipe faria — sem inventar informações. A IA mantém o contexto e direciona para vendas, suporte ou agendamento conforme a necessidade. Também oferecemos <Link to="/instagram/agente-ia" className="text-primary hover:underline">agente para Instagram</Link> no mesmo painel.
            </p>
          </section>

          <p className="text-sm text-muted-foreground mb-6">
            Veja também <Link to="/qualificacao-de-leads-no-whatsapp" className="text-primary hover:underline">qualificação de leads no WhatsApp</Link> e <Link to="/integracoes/crm" className="text-primary hover:underline">CRM integrado</Link> para operação comercial completa.
          </p>

          <section className="flex flex-col sm:flex-row gap-4 pt-6">
            <Button asChild size="lg" className="gap-2">
              <Link to="/planos">Ver planos <ArrowRight className="h-4 w-4" /></Link>
            </Button>
            <Button asChild variant="outline" size="lg">
              <Link to="/contato">Falar com a gente</Link>
            </Button>
          </section>
        </section>
      </main>

      <Footer />
    </div>
  );
}
